import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  normalizeCallMode,
  requiredMediaForMode,
  saveDeviceReadiness,
  getPersistentItem,
  setPersistentItem,
} from "../../util/deviceReadiness";
// Recording-indicator fix: this component's own getUserMedia() preview
// stream was only ever tracked in a local ref, cleaned up solely by this
// component's own unmount effect. If that unmount is ever skipped (e.g. a
// hard redirect elsewhere in the call-abort flow instead of a normal SPA
// route change), nothing else in the app knew this stream existed, so the
// browser tab's mic/camera indicator could stay lit indefinitely. Registering
// it here means the shared stopAllRegisteredMedia() safety net — already
// called on every call-end path and on Appointments list mount — will also
// catch and stop it even if this component's own cleanup did not run.
import { registerMediaStream } from "../../util/mediaTrackRegistry";
import { toast } from "react-toastify";

function stopStream(stream) {
  try {
    stream?.getTracks?.().forEach((track) => track.stop());
  } catch (_) {
    /* ignore */
  }
}

function friendlyDeviceLabel(device, index, kind) {
  let raw = String(device?.label || "").trim();
  const fallback =
    kind === "audiooutput"
      ? `Speaker ${index + 1}`
      : kind === "audioinput"
        ? `Microphone ${index + 1}`
        : `Camera ${index + 1}`;
  if (!raw) return fallback;

  // Clean technical names and brand marks
  raw = raw.replace(/\(R\)|\(TM\)/gi, "");
  raw = raw.replace(/\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)/g, ""); // clean (hex:hex) tech details
  raw = raw.replace(/\s+/g, " ").trim();

  const lower = raw.toLowerCase();
  let cleanName = raw;

  // Make brand and technical names cleaner and friendlier
  if (lower.includes("conexant")) {
    const isMic = kind === "audioinput";
    cleanName = isMic ? "Built-in Microphone (Conexant)" : "Built-in Speakers (Conexant)";
  } else if (lower.includes("realtek")) {
    const isMic = kind === "audioinput";
    cleanName = isMic ? "Built-in Microphone (Realtek)" : "Built-in Speakers (Realtek)";
  } else if (lower.includes("intel") && lower.includes("audio")) {
    const isMic = kind === "audioinput";
    cleanName = isMic ? "Built-in Microphone (Intel)" : "Built-in Speakers (Intel)";
  } else if (lower.includes("synaptics")) {
    const isMic = kind === "audioinput";
    cleanName = isMic ? "Built-in Microphone (Synaptics)" : "Built-in Speakers (Synaptics)";
  } else if (lower.includes("built-in") || lower.includes("internal")) {
    const isMic = kind === "audioinput";
    cleanName = isMic ? "Built-in Microphone" : "Built-in Speakers";
  } else if (lower.includes("integrated camera") || lower.includes("internal camera") || lower.includes("front camera") || lower.includes("webcam")) {
    cleanName = "Built-in Webcam";
  }

  // Formatting System Default / System Communications
  if (lower.startsWith("default")) {
    const rest = cleanName.replace(/^(default)\s*[-–:]?\s*/i, "").trim();
    const typeLabel = kind === "audioinput" ? "Microphone" : "Speaker";
    return rest ? `System Default — ${rest}` : `System Default (${typeLabel})`;
  }
  if (lower.startsWith("communications")) {
    const rest = cleanName.replace(/^(communications)\s*[-–:]?\s*/i, "").trim();
    const typeLabel = kind === "audioinput" ? "Microphone" : "Speaker";
    return rest ? `System Communications — ${rest}` : `System Communications (${typeLabel})`;
  }

  return cleanName;
}

async function playSpeakerTest(deviceId) {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return false;
  const audio = new Audio();
  try {
    if (deviceId && typeof audio.setSinkId === "function") {
      await audio.setSinkId(deviceId);
    }
  } catch (_) {
    /* setSinkId is optional */
  }
  const ctx = new AudioContext();
  try {
    if (ctx.state === "suspended") await ctx.resume();

    // Play a dual-tone pleasant synth chime instead of a single flat beep
    const playChime = async (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.08, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    const now = ctx.currentTime;
    await playChime(523.25, now, 0.3); // C5
    await playChime(659.25, now + 0.15, 0.3); // E5
    await playChime(783.99, now + 0.3, 0.4); // G5

    await new Promise((resolve) => setTimeout(resolve, 800));
    return true;
  } catch (_) {
    return false;
  } finally {
    try {
      await ctx.close();
    } catch (_) {
      /* ignore */
    }
  }
}

function mediaErrorMessage(error, needsVideo) {
  const name = error?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return needsVideo
      ? "Camera and microphone access is blocked. Please allow both in your browser's site settings to proceed."
      : "Microphone access is blocked. Please allow it in your browser's site settings to proceed.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return needsVideo
      ? "A working camera and microphone are required for this video consultation."
      : "A working microphone is required for this voice consultation.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "The required device is busy or unavailable. Close other camera or microphone apps, then retry.";
  }
  return "We could not start the required devices. Check your browser permissions and try again.";
}

export default function PreCallDeviceCheck({
  open = false,
  mode = "video",
  consultantReadiness = false,
  onReady,
  onCancel,
}) {
  const normalizedMode = normalizeCallMode(mode);
  const requirements = requiredMediaForMode(normalizedMode);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraIdRef = useRef("");
  const microphoneIdRef = useRef("");
  const audioContextRef = useRef(null);
  const animationRef = useRef(null);
  const mountedRef = useRef(true);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [errorName, setErrorName] = useState("");
  const [devices, setDevices] = useState({ cameras: [], microphones: [], speakers: [] });
  const [cameraId, setCameraId] = useState("");
  const [microphoneId, setMicrophoneId] = useState("");
  const [speakerId, setSpeakerId] = useState("");
  const [speakerTested, setSpeakerTested] = useState(false);
  const [isPlayingSpeakerTest, setIsPlayingSpeakerTest] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [cameraFailed, setCameraFailed] = useState(false);

  const releaseDevices = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
      audioContextRef.current = null;
    }
    stopStream(streamRef.current);
    const oldStream = streamRef.current;
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    } else if (oldStream) {
      try {
        document.querySelectorAll("video").forEach((vid) => {
          if (vid.srcObject === oldStream) {
            vid.srcObject = null;
          }
        });
      } catch (_) {}
    }
    if (mountedRef.current) setMicLevel(0);
  }, []);

  const startMeter = useCallback((stream) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      const source = context.createMediaStreamSource(stream);
      source.connect(analyser);
      const values = new Uint8Array(analyser.frequencyBinCount);
      audioContextRef.current = context;
      const tick = () => {
        analyser.getByteFrequencyData(values);
        const average = values.reduce((sum, value) => sum + value, 0) / values.length;
        if (mountedRef.current) setMicLevel(Math.min(100, Math.round(average * 1.6)));
        animationRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (_) {
      /* Meter is helpful, but not required for permission validation. */
    }
  }, []);

  const updateDeviceList = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const listed = await navigator.mediaDevices.enumerateDevices();
      if (!mountedRef.current) return;
      const cameras = listed.filter((device) => device.kind === "videoinput");
      const microphones = listed.filter((device) => device.kind === "audioinput");
      const speakers = listed.filter((device) => device.kind === "audiooutput");
      setDevices({ cameras, microphones, speakers });

      // Retrieve saved devices using getPersistentItem helper
      const savedCameraId = getPersistentItem("prontopsy_last_camera_id");
      const savedMicId = getPersistentItem("prontopsy_last_microphone_id");
      const savedSpeakerId = getPersistentItem("prontopsy_last_speaker_id");

      // CAMERA selection & fallback
      if (!cameraIdRef.current) {
        if (savedCameraId && cameras.some((c) => c.deviceId === savedCameraId)) {
          cameraIdRef.current = savedCameraId;
          setCameraId(savedCameraId);
        } else {
          if (savedCameraId && cameras.length > 0) {
            const savedLabel = getPersistentItem("prontopsy_last_camera_label") || "Previously saved camera";
            toast.warn(`Saved camera "${savedLabel}" is no longer available. Fell back to system default.`, { toastId: "cam_fallback" });
          }
          if (cameras[0]?.deviceId) {
            cameraIdRef.current = cameras[0].deviceId;
            setCameraId(cameras[0].deviceId);
          }
        }
      }

      // MICROPHONE selection & fallback
      if (!microphoneIdRef.current) {
        if (savedMicId && microphones.some((m) => m.deviceId === savedMicId)) {
          microphoneIdRef.current = savedMicId;
          setMicrophoneId(savedMicId);
        } else {
          if (savedMicId && microphones.length > 0) {
            const savedLabel = getPersistentItem("prontopsy_last_microphone_label") || "Previously saved microphone";
            toast.warn(`Saved microphone "${savedLabel}" is no longer available. Fell back to system default.`, { toastId: "mic_fallback" });
          }
          if (microphones[0]?.deviceId) {
            microphoneIdRef.current = microphones[0].deviceId;
            setMicrophoneId(microphones[0].deviceId);
          }
        }
      }

      // SPEAKER selection & fallback
      if (!speakerId) {
        if (savedSpeakerId && speakers.some((s) => s.deviceId === savedSpeakerId)) {
          setSpeakerId(savedSpeakerId);
        } else {
          if (savedSpeakerId && speakers.length > 0) {
            const savedLabel = getPersistentItem("prontopsy_last_speaker_label") || "Previously saved speaker";
            toast.warn(`Saved speaker "${savedLabel}" is no longer available. Fell back to system default.`, { toastId: "speaker_fallback" });
          }
          if (speakers[0]?.deviceId) {
            setSpeakerId(speakers[0].deviceId);
          }
        }
      }
    } catch (e) {
      console.error("Failed to enumerate devices:", e);
    }
  }, [speakerId]);

  const checkDevices = useCallback(async () => {
    if (!open) return;
    releaseDevices();
    setStatus("checking");
    setError("");
    setErrorName("");
    setCameraFailed(false);

    // Initial check of available devices
    await updateDeviceList();

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("MEDIA_UNSUPPORTED");
      }
      let stream;
      let liveAudio = false;
      let liveVideo = false;
      let hasCameraError = false;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: microphoneIdRef.current
            ? { deviceId: { exact: microphoneIdRef.current } }
            : true,
          video: requirements.video
            ? cameraIdRef.current
              ? { deviceId: { exact: cameraIdRef.current } }
              : true
            : false,
        });
        if (!open || !mountedRef.current) {
          stopStream(stream);
          return;
        }
        liveAudio = stream.getAudioTracks().some((track) => track.readyState === "live");
        liveVideo = !requirements.video || stream.getVideoTracks().some((track) => track.readyState === "live");
        if (!liveAudio || (requirements.video && !liveVideo)) {
          stopStream(stream);
          const missing = !liveAudio ? "microphone" : "camera";
          throw new Error(`MISSING_${missing.toUpperCase()}`);
        }
      } catch (err) {
        // If camera failed and we are setting consultant readiness, fall back to audio check
        if (requirements.video && consultantReadiness) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              audio: microphoneIdRef.current
                ? { deviceId: { exact: microphoneIdRef.current } }
                : true,
              video: false,
            });
            if (!open || !mountedRef.current) {
              stopStream(stream);
              return;
            }
            liveAudio = stream.getAudioTracks().some((track) => track.readyState === "live");
            liveVideo = false;
            hasCameraError = true;
            if (!liveAudio) {
              stopStream(stream);
              throw new Error("MISSING_MICROPHONE");
            }
          } catch (audioErr) {
            throw err; // throw original camera error
          }
        } else {
          throw err;
        }
      }

      if (mountedRef.current) {
        setCameraFailed(hasCameraError);
      }

      streamRef.current = stream;
      registerMediaStream(stream);
      if (!hasCameraError && requirements.video && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => { });
      }
      startMeter(stream);
      setStatus("ready");

      // Update device list again to get actual labels after permission was granted
      await updateDeviceList();
    } catch (err) {
      console.error("Pre-call device check failed:", err);
      if (mountedRef.current) {
        setStatus("error");
        setErrorName(err?.name || err?.message || "");
        setError(mediaErrorMessage(err, requirements.video));
      }
      // Even if failed, call updateDeviceList to show whatever devices we can discover
      await updateDeviceList();
    }
  }, [open, releaseDevices, requirements.video, startMeter, consultantReadiness, updateDeviceList]);

  useEffect(() => {
    mountedRef.current = true;
    if (open) checkDevices();
    return () => {
      mountedRef.current = false;
      releaseDevices();
    };
  }, [open, checkDevices, releaseDevices]);

  if (!open) return null;

  const ready = status === "ready";
  const permissionBlocked = ["NotAllowedError", "PermissionDeniedError"].includes(errorName);

  // Custom headers and titles for a professional configuration tool
  const title = consultantReadiness
    ? "Device configuration & setup"
    : normalizedMode === "voice"
      ? "Check your microphone"
      : "Check your camera and microphone";

  const detail = consultantReadiness
    ? "Configure and test your multimedia devices to ensure seamless audio and video calls."
    : normalizedMode === "voice"
      ? "Only microphone permission is requested for this voice consultation."
      : "Camera and microphone are mandatory for this video consultation.";

  // Detect if consultant is already online to show friendlier "Done" button
  let userDetailsObj = null;
  try {
    userDetailsObj = JSON.parse(localStorage.getItem("userDetails") || "{}");
  } catch (_) { }
  const isCurrentlyOnline = userDetailsObj?.onlineStatus === "online";

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center overflow-y-auto bg-slate-950/80 p-3 sm:p-4 backdrop-blur-md">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl border border-slate-100 transition-all duration-300">

        {/* Header Section */}
        <div className="p-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#002C6B]/10 text-[#002C6B]">
              <i className={`fa-solid ${normalizedMode === "voice" ? "fa-microphone" : "fa-desktop"} text-lg`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{title}</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">{detail}</p>
            </div>
          </div>
        </div>

        {/* Media Preview Section */}
        <div className="p-6 space-y-5">
          {requirements.video ? (
            <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-900 border border-slate-800 shadow-inner">
              <video ref={videoRef} muted playsInline autoPlay className="h-full w-full object-cover" />
              {(status !== "ready" || cameraFailed) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 text-sm font-medium text-slate-300 bg-slate-950/70 backdrop-blur-sm">
                  {cameraFailed ? (
                    <>
                      <i className="fa-solid fa-video-slash mb-2 text-2xl text-rose-500" />
                      <p className="font-semibold text-slate-200">Camera is unavailable</p>
                      <p className="text-xs text-slate-400 mt-1 max-w-sm">Video calls will be temporarily disabled. You can still proceed with Voice calls and Chat.</p>
                    </>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                      {status === "checking" ? "Starting camera preview…" : "Camera preview unavailable"}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-[140px] items-center justify-center rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 text-white border border-slate-800 shadow-inner">
              <div className="text-center">
                <i className="fa-solid fa-microphone mb-3 text-3xl text-blue-400 animate-pulse" />
                <p className="text-sm font-semibold tracking-wide text-slate-200">Voice consultation — Camera is inactive</p>
              </div>
            </div>
          )}

          {/* Controls & Inputs */}
          <div className="space-y-4">
            {/* Mic Meter */}
            <div>
              <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-600">
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-signal text-[10px] text-slate-400" />
                  Microphone Level
                </span>
                <span className={ready ? "text-emerald-600" : "text-amber-500"}>
                  {ready ? "Active" : "Connecting..."}
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-100 border border-slate-200/50 p-0.5">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-[width] duration-75"
                  style={{ width: `${ready ? Math.max(2, micLevel) : 0}%` }}
                />
              </div>
            </div>

            {/* Select Dropdowns */}
            <div className="grid gap-4 sm:grid-cols-2 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">

              {/* Camera Selector */}
              <div className="flex flex-col">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <i className="fa-solid fa-video text-[10px]" /> Camera
                </label>
                {devices.cameras.length > 0 ? (
                  <select
                    value={cameraId}
                    onChange={(event) => {
                      const val = event.target.value;
                      cameraIdRef.current = val;
                      setCameraId(val);
                      checkDevices();
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    {devices.cameras.map((device, index) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {friendlyDeviceLabel(device, index, "videoinput")}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    disabled
                    className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-500 shadow-sm cursor-not-allowed">
                    <option>No cameras detected</option>
                  </select>
                )}
              </div>

              {/* Mic Selector */}
              <div className="flex flex-col">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <i className="fa-solid fa-microphone text-[10px]" /> Microphone
                </label>
                {devices.microphones.length > 0 ? (
                  <select
                    value={microphoneId}
                    onChange={(event) => {
                      const val = event.target.value;
                      microphoneIdRef.current = val;
                      setMicrophoneId(val);
                      checkDevices();
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    {devices.microphones.map((device, index) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {friendlyDeviceLabel(device, index, "audioinput")}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    disabled
                    className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-500 shadow-sm cursor-not-allowed">
                    <option>No microphones detected</option>
                  </select>
                )}
              </div>

              {/* Speaker Selector */}
              <div className="flex flex-col sm:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <i className="fa-solid fa-volume-high text-[10px]" /> Speaker / Audio Output
                </label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  {devices.speakers.length > 0 ? (
                    <select
                      value={speakerId}
                      onChange={(event) => setSpeakerId(event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                      {devices.speakers.map((device, index) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {friendlyDeviceLabel(device, index, "audiooutput")}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      disabled
                      className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-500 shadow-sm cursor-not-allowed">
                      <option>No audio output devices detected</option>
                    </select>
                  )}
                  <button
                    type="button"
                    disabled={isPlayingSpeakerTest || devices.speakers.length === 0}
                    onClick={async () => {
                      setIsPlayingSpeakerTest(true);
                      const ok = await playSpeakerTest(speakerId);
                      setSpeakerTested(ok);
                      setIsPlayingSpeakerTest(false);
                    }}
                    className={`shrink-0 rounded-xl px-4 py-2.5 text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-2 border ${devices.speakers.length === 0
                      ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
                      : isPlayingSpeakerTest
                        ? "bg-blue-50 border-blue-200 text-[#002C6B] cursor-wait"
                        : speakerTested
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                          : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                      }`}>
                    {isPlayingSpeakerTest ? (
                      <>
                        <span className="flex h-2 w-2 relative">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                        </span>
                        Playing...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-circle-play" />
                        {speakerTested ? "Test Sound Played" : "Play Test Sound"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Error Message Box */}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4 text-sm text-rose-800 shadow-sm">
                <div className="flex gap-2">
                  <i className="fa-solid fa-triangle-exclamation mt-0.5" />
                  <div>
                    <p className="font-bold">
                      {permissionBlocked
                        ? requirements.video
                          ? "Camera and microphone access is restricted"
                          : "Microphone access is restricted"
                        : "Device initialization failed"}
                    </p>
                    <p className="mt-1 opacity-90 text-xs">{error}</p>
                    {permissionBlocked && (
                      <ol className="mt-3 list-decimal space-y-1.5 pl-4 text-xs leading-relaxed opacity-90">
                        <li>Locate the padlock, camera, or settings icon in your browser address bar.</li>
                        <li>
                          Change permissions for {requirements.video ? "Camera and Microphone" : "Microphone"} to <strong>Allow</strong>.
                        </li>
                        <li>Click “Retry permission check” below.</li>
                      </ol>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Section */}
        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={() => {
                releaseDevices();
                onCancel();
              }}
              className="rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 border border-slate-200 shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all">
              Cancel
            </button>
          )}
          {!ready ? (
            <button
              type="button"
              onClick={checkDevices}
              disabled={status === "checking"}
              className="rounded-xl bg-[#002C6B] px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#001f4d] active:scale-[0.98] disabled:cursor-wait disabled:opacity-60 transition-all">
              {status === "checking" ? (
                <span className="flex items-center gap-2">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-solid border-white border-t-transparent" />
                  Checking...
                </span>
              ) : (
                "Retry permission check"
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                // Save last successfully tested devices
                if (cameraId && devices.cameras.length > 0) {
                  setPersistentItem("prontopsy_last_camera_id", cameraId);
                  const label = devices.cameras.find((d) => d.deviceId === cameraId)?.label || "";
                  setPersistentItem("prontopsy_last_camera_label", label);
                }
                if (microphoneId && devices.microphones.length > 0) {
                  setPersistentItem("prontopsy_last_microphone_id", microphoneId);
                  const label = devices.microphones.find((d) => d.deviceId === microphoneId)?.label || "";
                  setPersistentItem("prontopsy_last_microphone_label", label);
                }
                if (speakerId && devices.speakers.length > 0) {
                  setPersistentItem("prontopsy_last_speaker_id", speakerId);
                  const label = devices.speakers.find((d) => d.deviceId === speakerId)?.label || "";
                  setPersistentItem("prontopsy_last_speaker_label", label);
                }

                saveDeviceReadiness({ audio: true, video: requirements.video && !cameraFailed });
                releaseDevices();
                onReady?.({
                  mode: normalizedMode,
                  hasMicrophone: true,
                  hasCamera: requirements.video && !cameraFailed,
                });
              }}
              className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 active:scale-[0.98] transition-all">
              {consultantReadiness
                ? isCurrentlyOnline
                  ? "Done & Close"
                  : "Go Online — devices ready"
                : "Join consultation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
